import {
	expressQuerySelector,
	expressQuerySelectorAll,
	expressRemoveClass,
	getParentWithClassName,
} from "../../common/html";
import { createUsps } from "../molecules/usp";
import { createCustomSlider, ICustomPhotoSliderItem, ICustomPhotoSliderWithDivSettings } from "./customslider";
import { IRecommendationEventParams, trackRecommendationCartEvent } from "../../common/events";
import { stopSkeletonLoading } from "./skeleton";
import { initVideoPlayer } from "./video-player";
import { createProductGrid } from "../molecules/product-grid";
import { createHighlightedTiles } from "../organisms/highlighted-tiles";
import { createImageDescription } from "../molecules/image-description";
import { createTruncate } from "./truncate";
import { createNewsletterSubscribe, createNewsletterUnsubscribe } from "../molecules/newsletter";
import { getParameter, updateParameter } from "../../utils/querystring";
import { logError } from "../../services/logger-service";
import { createGtmMutationObserver } from "./gtm-mutation-observer";
import { getHtmlRequest } from "../../utils/fetch";
import { getWishlistItems } from "../../services/wishlist-service";

export async function createHomepageElements() {
	window.showMores = [];
	// load the wishlistItems
	window.wishlist = await getWishlistItems();
	const uspDesktopEls = expressQuerySelectorAll<HTMLElement>(document, ".technical-usp-desktop");

	// Initialize the usp's
	if (uspDesktopEls) {
		uspDesktopEls.forEach((uspDesktopEl) => createUsps(uspDesktopEl));
	}

	window["initProductSliderComponent"] = initProductSliderComponent;

	// init not load on demand slider components
	const sliderEl = expressQuerySelectorAll(document, ".technical-slider");
	sliderEl.forEach((x) => window["initProductSliderComponent"](x));

	// Fallback for the Emarsys Recommendations
	const recommendationsEl = expressQuerySelector<HTMLElement>(document, ".technical-emarsys-recommendation", false);
	if (recommendationsEl) {
		createGtmMutationObserver(recommendationsEl, 2).catch((err) => {
			const fallbackRecommendationsEl = expressQuerySelector<HTMLElement>(
				document,
				".technical-recommendations-fallback-slider",
				false,
			);
			if (fallbackRecommendationsEl) {
				const sliderContentKey = fallbackRecommendationsEl.dataset.contentkey || "";
				const showPrices = fallbackRecommendationsEl.dataset.showprice === "true";
				const showCurrency = window.context.removeCurrencySymbolFormUpperFunnelPages === "false";
				if (sliderContentKey) {
					getHtmlRequest(
						`/apicore/componentapi/productslider?contentkey=${sliderContentKey}&isProspect=false&hidePrices=${!showPrices}&showCurrency=${showCurrency}`,
						{
							headers: new Headers({ "Content-type": "application/json; charset=utf-8" }),
						},
					).then((r) => {
						const elementHtml = document.createRange().createContextualFragment(r);
						fallbackRecommendationsEl.appendChild(elementHtml);
						const fallbackSliderEl = expressQuerySelector<HTMLElement>(document, ".technical-slider", false);
						if (fallbackSliderEl) {
							expressRemoveClass(fallbackRecommendationsEl, "u-hide");
							initProductSliderComponent(fallbackSliderEl);
						}
					});
				}
			}
		});
	}

	// init highlighted tiles
	const highlightedTilesEl = expressQuerySelectorAll<HTMLElement>(document, ".technical-highlighted-tiles");
	highlightedTilesEl.forEach((x) => createHighlightedTiles(x));

	const headerComponents = expressQuerySelectorAll<HTMLElement>(document, ".technical-header-component");
	headerComponents.forEach((header) => {
		const truncateEl = expressQuerySelector<HTMLElement>(header, ".technical-shave", false);
		if (!truncateEl) return;
		createTruncate(truncateEl, { lines: 2 });
	});

	window["initProductGridComponent"] = function initProductGridComponent(containerEl: HTMLElement) {
		const gridEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-product-grid", true);
		if (gridEl) createProductGrid(gridEl, {}, { refreshOnResize: true });
	};
	window["initMaterialTilesComponent"] = function initMaterialTilesComponent(containerEl: HTMLElement) {
		createImageDescription(containerEl);
		const videoTileEls = expressQuerySelectorAll<HTMLElement>(
			containerEl,
			".technical-video-tile, .technical-video-container",
		);
		videoTileEls.forEach((el) => {
			if (!getParentWithClassName(el, "technical-product-grid-content")) initVideoPlayer(el);
		});

		const newsletterSubscribeEls = expressQuerySelectorAll<HTMLElement>(
			containerEl,
			".m-material-tiles .technical-newsletter-subscribe",
		);
		newsletterSubscribeEls.forEach((el) => createNewsletterSubscribe(el));

		const newsletterUnSubscribeEls = expressQuerySelectorAll<HTMLElement>(
			containerEl,
			".m-material-tiles .technical-newsletter-unsubscribe",
		);
		newsletterUnSubscribeEls.forEach((el) => createNewsletterUnsubscribe(el));
	};

	const productGrids = expressQuerySelectorAll(
		document,
		":not([data-component-callback=initProductGridComponent]) .technical-grid-and-filters",
	);
	productGrids.forEach((x) => window["initProductGridComponent"](x));

	const materialTiles = expressQuerySelectorAll(
		document,
		":not([data-component-callback=initMaterialTilesComponent]) .technical-image-description",
	);
	materialTiles.forEach((x) => window["initMaterialTilesComponent"](x));

	const videoElements = expressQuerySelectorAll<HTMLElement>(document, ".technical-video");
	videoElements.forEach((x) => initVideoPlayer(x));
	stopSkeletonLoading();

	if (window.location.hash) {
		const elContainerEl = expressQuerySelector<HTMLElement>(document, ".technical-homepage-elements", true);
		const hashEl = expressQuerySelector<HTMLElement>(elContainerEl, window.location.hash, false);
		if (hashEl) hashEl.scrollIntoView({ behavior: "smooth" });
		else
			logError(
				"Can't scroll to item with id = " + window.location.hash + " as it can't be found on the page",
				window.location.href,
				undefined,
				undefined,
				undefined,
				"Content",
			);
	}
}

export function initProductSliderComponent(containerEl: HTMLElement, epc?: string) {
	const sliderEl = containerEl.classList.contains("technical-slider")
		? containerEl
		: expressQuerySelector(containerEl, ".technical-slider", true);

	// convert so slider elements
	const sliderElements: ICustomPhotoSliderItem[] = [];
	for (let z = 0, l = sliderEl.children.length; z < l; z++) {
		const slideEl = sliderEl.children[z] as HTMLElement;
		if (epc) {
			const link = expressQuerySelector<HTMLAnchorElement>(slideEl, "a", false);
			if (link) {
				link.href = updateParameter("epc", epc, link.href);
			}
		}
		sliderElements.push({ template: slideEl });
	}

	// settings for slider
	const settings = {
		sliderElements: sliderElements,
		sliderContainerSettings: {
			extraSliderClass: "m-product-slider__slider-container",
			elementsContainerSettings: {
				extraSliderClass: "m-product-slider__slider-content",
				slideClicked: (e: Event, settings: ICustomPhotoSliderWithDivSettings) => {
					const slider = getParentWithClassName<HTMLElement>(e.target as Element, "technical-recommendations");
					if (!slider) return; // only have on click on recommendations

					const currentEl = getParentWithClassName<HTMLElement>(e.target as Element, "technical-slide");
					if (!currentEl) return;

					const anchorEl = expressQuerySelector<HTMLAnchorElement>(currentEl, "a");
					const label = {
						pageType: window.context.pageType,
						pageCode: window.context.pageName,
						pvc: getParameter("pvc", anchorEl.href, false) || "",
					} as IRecommendationEventParams;
					trackRecommendationCartEvent("Recommended product clicked", label);
				},
			},
		},
	} as ICustomPhotoSliderWithDivSettings;

	// create slider
	if (sliderEl.parentElement) createCustomSlider(sliderEl.parentElement, settings);
}
